import React, { useEffect, useState, useRef, useContext } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Grid2 from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import FacilityServices from "./FacilityServices";
import StaffSettings from "./StaffSettings";
import ServiceEdit from "./EditService";
import NewServiceForm from "./NewService";
import NewUser from "./NewUser";
import EditUser from "./EditUser";
import FacilityBasicSettings from "./BasicSettings";
import AuthContext from "../Context/AuthProvider";
import PredefinedTextList from "./PredefinedTextList";
// import PrescriptionSettings from "./PrescriptionSettings";

function MyFacilityHome() {
  const facilityServicesRef = useRef();
  const staffSettingsRef = useRef();
  const authContext = useContext(AuthContext); // Get the auth context
  const [facilityOptions, setFacilityOptions] = useState([]); // You'll need to populate this with your facility data

  // Define all functions before they are used
  const handleNewServiceTab = () => {
    const newTabNumber = newServiceTabNumber + 1;
    setNewServiceTabNumber(newTabNumber);

    const newTab = {
      label: `New Service (${newTabNumber})`,
      value: `NewService-${newTabNumber}`,
      type: "NewService",
      tempId: `NewService-${newTabNumber}`,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setValue(newTab.value);
  };

  const handleEditServiceDetails = (serviceData) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find(
        (tab) => tab.value === `ServiceEdit-${serviceData.id}`
      );

      if (existingTab) {
        setValue(existingTab.value);
        return prevTabs;
      }

      const newTab = {
        label: `Edit ${serviceData.name}`,
        value: `ServiceEdit-${serviceData.id}`,
        type: "ServiceEdit",
        serviceDetails: serviceData,
      };

      setValue(newTab.value);
      return [...prevTabs, newTab];
    });
  };

  const handleNewUser = () => {
    const newTabNumber = newUserTabNumber + 1;
    setNewUserTabNumber(newTabNumber);

    // Create a unique id for this new user tab
    const newUserTabId = `NewUser-${newTabNumber}`;

    // Check if tab already exists (shouldn't happen with incrementing numbers)
    const existingTab = tabs.find((tab) => tab.value === newUserTabId);

    if (existingTab) {
      // If somehow it exists, just activate it
      setValue(existingTab.value);
    } else {
      // Create a new tab with unique id
      const newTab = {
        label: `New User (${newTabNumber})`,
        value: newUserTabId,
        type: "NewUser",
        tempId: newUserTabId,
      };

      setTabs((prevTabs) => [...prevTabs, newTab]);
      setValue(newUserTabId);
    }
  };

  const handleStaffViewDetails = (userData) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find(
        (tab) => tab.value === `UserInfo-${userData.id}`
      );

      if (existingTab) {
        setValue(existingTab.value);
        return prevTabs;
      }

      const newTab = {
        label: `User: ${userData.firstName} ${userData.lastName}`,
        value: `UserInfo-${userData.id}`,
        type: "UserInfo",
        userData: userData,
      };

      setValue(newTab.value);
      return [...prevTabs, newTab];
    });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleCloseTab = (tabValue) => {
    if (
      [
        "BasicSettings",
        "FacilityServices",
        "StaffSettings",
        "PredefinedTextList",
          "PrescriptionSettings",
      ].includes(tabValue)
    )
      return;

    setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tabValue));
    if (value === tabValue) {
      setValue("BasicSettings");
    }
  };

  const onNewServiceAdded = (tempId) => {
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tempId));
    setValue("FacilityServices");
    facilityServicesRef?.current?.refreshServicesList();
  };

  const onNewUserAdded = (tempId) => {
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tempId));
    setValue("StaffSettings");
    staffSettingsRef?.current?.refreshUsersList();
  };

  // State Initialization
  const [value, setValue] = useState(() => {
    const savedActiveTab = localStorage.getItem("facilityActiveTab");
    return savedActiveTab || "BasicSettings";
  });

  const [tabs, setTabs] = useState(() => {
    // Load tabs from local storage
    const savedTabs = localStorage.getItem("facility-tabs");
    return savedTabs
      ? JSON.parse(savedTabs)
      : [
          {
            label: "Basic Settings",
            value: "BasicSettings",
            type: "BasicSettings",
          },
          {
            label: "Facility Services",
            value: "FacilityServices",
            type: "FacilityServices",
          },
          {
            label: "Staff Settings",
            value: "StaffSettings",
            type: "StaffSettings",
          },
          {
            label: "Predefined Text List",
            value: "PredefinedTextList",
            type: "PredefinedTextList",
          },
        //   {
        //   label: "Prescription Settings",
        //   value: "PrescriptionSettings",
        //   type: "PrescriptionSettings",
        // },
        ];
  });

  const [newUserTabNumber, setNewUserTabNumber] = useState(0);
  const [newServiceTabNumber, setNewServiceTabNumber] = useState(0);

  // Effect Hooks
  useEffect(() => {
    localStorage.setItem("facility-tabs", JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem("facilityActiveTab", value);
  }, [value]);

  // You might want to fetch facility options when the component mounts
  useEffect(() => {
    // Add your facility data fetching logic here
    // For example:
    // async function fetchFacilities() {
    //   const response = await getFacilities(authContext);
    //   if (response.isSuccess) {
    //     setFacilityOptions(response.data);
    //   }
    // }
    // fetchFacilities();
  }, []);

  return (
    <Grid2 container>
      <TabContext value={value}>
        <Grid2
          container
          alignItems="center"
          spacing={1}
          sx={{ borderBottom: "1px solid lightgray", width: "100%" }}
        >
          <Tabs value={value} onChange={handleChange} variant="scrollable">
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Grid2 display="flex" alignItems="center">
                    {tab.label}
                    {![
                      "BasicSettings",
                      "FacilityServices",
                      "StaffSettings",
                      "PredefinedTextList",
                      "PrescriptionSettings",
                    ].includes(tab.value) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseTab(tab.value);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Grid2>
                }
                value={tab.value}
              />
            ))}
          </Tabs>
        </Grid2>

        <Grid2 item>
          {tabs.map((tab) => (
            <TabPanel
              key={tab.value}
              value={tab.value}
              keepMounted={true}
              sx={{ padding: "0px", width: "100%" }}
            >
              {tab.type === "BasicSettings" && <FacilityBasicSettings />}
              {tab.type === "FacilityServices" && (
                <FacilityServices
                  ref={facilityServicesRef}
                  onNewService={handleNewServiceTab}
                  onEditDetails={handleEditServiceDetails}
                />
              )}
              {tab.type === "StaffSettings" && (
                <StaffSettings
                  ref={staffSettingsRef}
                  onNewUser={handleNewUser}
                  onViewDetails={handleStaffViewDetails}
                />
              )}

              {tab.type === "PredefinedTextList" && <PredefinedTextList />}
              {/* {tab.type === "PrescriptionSettings" && <PrescriptionSettings />} */}

              {tab.type === "ServiceEdit" && (
                <ServiceEdit
                  key={tab.serviceDetails.id}
                  serviceDetails={tab.serviceDetails}
                />
              )}
              {tab.type === "NewService" && (
                <NewServiceForm
                  tempId={tab.tempId}
                  onNewServiceAdded={onNewServiceAdded}
                />
              )}
              {tab.type === "UserInfo" && (
                <EditUser key={tab.userData.id} user={tab.userData} />
              )}
              {tab.type === "NewUser" && (
                <NewUser
                  tempId={tab.tempId}
                  onNewUserAdded={onNewUserAdded}
                  authContext={authContext}
                  facilityOptions={facilityOptions}
                />
              )}
            </TabPanel>
          ))}
        </Grid2>
      </TabContext>
    </Grid2>
  );
}

export default MyFacilityHome;